import { useEffect, useState } from 'react';

import * as UTIF from 'utif';

const useTiffLoader = (src, pageBlobs) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(Boolean(src || (pageBlobs && pageBlobs.length > 0)));
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(Boolean(src || (pageBlobs && pageBlobs.length > 0)));
  }, [src, pageBlobs]);

  useEffect(() => {
    const createdUrls = [];

    const cleanupUrls = () => {
      createdUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Silent cleanup
        }
      });
    };

    if (pageBlobs && pageBlobs.length > 0) {
      setLoading(true);
      setError(null);

      const loadBlobPages = async () => {
        try {
          const blobPages = [];

          for (let i = 0; i < pageBlobs.length; i++) {
            const blob = pageBlobs[i];
            const imageUrl = URL.createObjectURL(blob);
            createdUrls.push(imageUrl);

            const img = await new Promise((resolve, reject) => {
              const image = new Image();
              image.onload = () => resolve(image);
              image.onerror = reject;
              image.src = imageUrl;
            });

            blobPages.push({
              isBlob: true,
              imageUrl,
              width: img.width,
              height: img.height
            });
          }

          setPages(blobPages);
          setLoading(false);
        } catch (err) {
          setError('Failed to load pages');
          setLoading(false);
        }
      };

      loadBlobPages();
      return cleanupUrls;
    }

    if (!src) return;
    const abortController = new AbortController();

    const loadTiff = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(src, { signal: abortController.signal });
        const arrayBuffer = await response.arrayBuffer();

        const view = new DataView(arrayBuffer);
        const byte0 = view.getUint8(0);
        const byte1 = view.getUint8(1);
        const isTiff = (byte0 === 0x49 && byte1 === 0x49) || (byte0 === 0x4d && byte1 === 0x4d);
        const isPng = byte0 === 0x89 && byte1 === 0x50;

        if (isPng) {
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
          const imageUrl = URL.createObjectURL(blob);
          createdUrls.push(imageUrl);

          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = imageUrl;
          });

          setPages([
            {
              isPng: true,
              imageUrl,
              width: img.width,
              height: img.height
            }
          ]);
          setLoading(false);
          return;
        }

        if (!isTiff) {
          throw new Error('Unsupported format');
        }

        const ifds = UTIF.decode(arrayBuffer);
        const decodedPages = [];
        for (let i = 0; i < ifds.length; i++) {
          const ifd = ifds[i];
          let width = ifd[256];
          let height = ifd[257];

          if (width && Array.isArray(width)) width = width[0];
          if (height && Array.isArray(height)) height = height[0];

          UTIF.decodeImage(arrayBuffer, ifd);

          if (!width) {
            width = ifd[256] || ifd.t256 || ifd.width;
            if (width && Array.isArray(width)) width = width[0];
          }
          if (!height) {
            height = ifd[257] || ifd.t257 || ifd.height;
            if (height && Array.isArray(height)) height = height[0];
          }

          if (width && height) {
            ifd.width = width;
            ifd.height = height;
          }
          decodedPages.push(ifd);
        }

        setPages(decodedPages);
        setLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Failed to load TIFF image');
          setLoading(false);
        }
      }
    };

    loadTiff();
    return () => {
      abortController.abort();
      cleanupUrls();
    };
  }, [src, pageBlobs]);

  return { pages, loading, error };
};

export default useTiffLoader;
